import type { AccessDenialReason } from 'v2/pages/misc/access-denied'
import { type RFeatureFlags, UserRole } from 'v2/types/api/auth'
import type { RUserProfile } from 'v2/types/api/settings'
import { type ModulePermissionTypeMap, RbacModules, RbacPermissionTypes } from 'v2/types/rbac'

export interface RouteMeta {
  roles?: UserRole[]
  profileData?: RUserProfile
  featureFlags?: RFeatureFlags
  permissionsMap?: ModulePermissionTypeMap
  isIndianRegion?: boolean
  isUsRegion?: boolean
}

export const isAdministrator = (meta?: RouteMeta): boolean => {
  return meta?.roles?.includes(UserRole.administrator) ?? false
}

export const isFinanceAnalyst = (meta?: RouteMeta): boolean => {
  return meta?.roles?.includes(UserRole.finance_analyst) ?? false
}

export const isDeveloper = (meta?: RouteMeta): boolean => {
  return meta?.roles?.includes(UserRole.developer) ?? false
}

export const isSupport = (meta?: RouteMeta): boolean => {
  return meta?.roles?.includes(UserRole.support) ?? false
}

export const isSupportOnly = (meta?: RouteMeta): boolean => {
  const roles = meta?.roles || []
  return (
    roles.includes(UserRole.support) &&
    !roles.includes(UserRole.administrator) &&
    !roles.includes(UserRole.developer)
  )
}

/**
 * UCC reviewer is a gated role restricted to the UCC view, treated like support-only:
 * only meaningful when the user isn't also an admin/developer.
 */
export const isUccReviewerOnly = (meta?: RouteMeta): boolean => {
  const roles = meta?.roles || []
  return roles.includes(UserRole.ucc_reviewer) && roles.length === 1
}

/**
 * Check if user is a human specialist (property-based, not role)
 * Human specialists should only see conversations and profile
 */
export const isHumanSpecialist = (meta?: RouteMeta): boolean => {
  // If human specialist feature is enabled AND user is marked as human agent
  return !!meta?.roles?.includes(UserRole.agent) && meta.roles?.length === 1
}

/**
 * Check if user is restricted from accessing routes (billing-based restriction)
 * Used for blocking conversation access when user is not human agent
 * Note: Seat billing is always-on for all orgs (no feature flag)
 */
export const isHumanAgentRestricted = (meta?: RouteMeta): boolean => {
  return !(meta?.profileData?.organizations?.[0]?.is_human_agent ?? false)
}

/**
 * Check if user has restricted role that needs special handling.
 * finance_analyst/support demote unless the user is also admin/developer; ucc_reviewer restricts
 * only when it is the sole role (combined with any other role, that role's access applies).
 */
const hasRestrictedRole = (meta?: RouteMeta): boolean => {
  const roles = meta?.roles || []
  const hasAdminOrDev = roles.includes(UserRole.administrator) || roles.includes(UserRole.developer)
  const hasFinanceOrSupport =
    roles.includes(UserRole.finance_analyst) || roles.includes(UserRole.support)

  return (hasFinanceOrSupport && !hasAdminOrDev) || isUccReviewerOnly(meta)
}

// Section visibility helpers
// Default: allow access (for agent, supervisor, admin, developer)
// Restrict: only for finance_analyst, support, and human specialists
export const canViewBuildSection = (meta?: RouteMeta): boolean => {
  // Human specialists cannot see BUILD section
  if (isHumanSpecialist(meta)) return false

  // If user has restricted role (finance or support without admin/dev), block
  if (hasRestrictedRole(meta)) return false

  // Otherwise allow (includes admin, developer, agent, supervisor)
  return true
}

export const canViewDeploySection = (meta?: RouteMeta): boolean => {
  // Human specialists cannot see DEPLOY section
  if (isHumanSpecialist(meta)) return false

  // If user has restricted role (finance or support without admin/dev), block
  if (hasRestrictedRole(meta)) return false

  // Otherwise allow (includes admin, developer, agent, supervisor)
  return true
}

export const canViewMonitorSection = (meta?: RouteMeta): boolean => {
  // Human specialists cannot see MONITOR section
  if (isHumanSpecialist(meta)) return false

  const roles = meta?.roles || []

  // Support can see Analytics and Logs (checked separately), but not other monitor items
  const hasSupportOnly =
    roles.includes(UserRole.support) &&
    !roles.includes(UserRole.administrator) &&
    !roles.includes(UserRole.developer)
  if (hasSupportOnly) return false

  // UCC reviewer is restricted to the UCC view — no monitor section
  if (isUccReviewerOnly(meta)) return false

  // Finance analyst cannot see monitor section
  const hasFinanceOnly =
    roles.includes(UserRole.finance_analyst) &&
    !roles.includes(UserRole.administrator) &&
    !roles.includes(UserRole.developer)
  if (hasFinanceOnly) return false

  // Otherwise allow (includes admin, developer, agent, supervisor)
  return true
}

// Does the current profile grant `permissionType` on the given RBAC module (org-level map)?
const hasModulePermission = (
  meta: RouteMeta | undefined,
  module: RbacModules,
  permissionType: RbacPermissionTypes,
): boolean => !!meta?.permissionsMap?.[module]?.[permissionType]

// Does the current profile grant `view` on the given RBAC module (org-level permission map)?
const hasModuleView = (meta: RouteMeta | undefined, module: RbacModules): boolean =>
  hasModulePermission(meta, module, RbacPermissionTypes.view)

/** The RBAC `flow` module: which roles get Telephony > Flow, and AI Agents when the org has them. */
export const canViewFlow = (meta?: RouteMeta): boolean => hasModuleView(meta, RbacModules.aiAgent)

// Support switches AI agents off per org, apart from the roles' `flow` permission.
export const areAgentsDisabled = (meta?: RouteMeta): boolean =>
  meta?.profileData?.organizations?.[0]?.ai_agents_disabled === true

/** AI Agents: every agent page, its deep links and the agent analytics gate on this. */
export const canViewAgents = (meta?: RouteMeta): boolean =>
  canViewFlow(meta) && !areAgentsDisabled(meta)

/** Telephony > Flow opens: the `flow` permission and the sidebar flag, assumed on while flags load. */
export const canOpenFlowSection = (meta?: RouteMeta): boolean =>
  canViewFlow(meta) &&
  Boolean(meta?.featureFlags?.isInitialData || meta?.featureFlags?.workspaceSidebarEnabled)

export type AgentWorkspace = 'agents' | 'flow'

/**
 * Where agent building happens: AI Agents, or the same builder under Telephony > Flow when the org has
 * agents off, or nowhere.
 */
export const agentWorkspace = (meta?: RouteMeta): AgentWorkspace | undefined => {
  if (canViewAgents(meta)) return 'agents'
  return canOpenFlowSection(meta) ? 'flow' : undefined
}

/** Which denial a Flow page shows: 'feature' for an administrator without `flow`. */
export const flowDenialReason = (meta?: RouteMeta): AccessDenialReason | undefined =>
  isAdministrator(meta) && !canViewFlow(meta) ? 'feature' : undefined

/** Which denial an AI-agent page shows: 'feature' (support can enable it) when the org has agents off. */
export const agentsDenialReason = (meta?: RouteMeta): AccessDenialReason | undefined =>
  areAgentsDisabled(meta) ? 'feature' : flowDenialReason(meta)

export const canViewLogs = (meta?: RouteMeta): boolean => {
  // Human specialists see only conversations + profile, regardless of RBAC
  if (isHumanSpecialist(meta)) return false

  // Visibility is driven by the RBAC `logs` module (the backend profile decides who gets it)
  return hasModuleView(meta, RbacModules.logs)
}

/**
 * May the current profile see unredacted PII in log responses? Driven by `logs`.`view_pii`.
 * Without it the backend replaces phone numbers with "[REDACTED]" and 403s the log-export
 * endpoints — so this is also what decides whether an export affordance is offered at all.
 */
export const canViewLogsPii = (meta?: RouteMeta): boolean =>
  hasModulePermission(meta, RbacModules.logs, RbacPermissionTypes.viewPii)

/**
 * UCC (India Unsolicited Commercial Communication) compliance is India-only and gated by the
 * RBAC `ucc` module. Shared by the Phone Numbers route config and the UCC tab.
 */
export const canViewUcc = (meta?: RouteMeta): boolean =>
  !!meta?.isIndianRegion && hasModuleView(meta, RbacModules.ucc)

/**
 * 10DLC / Toll-free compliance registration is US-messaging-only (the opposite region
 * polarity of UCC) and gated by the RBAC `message_channel` module. Shared by the
 * /compliance route config and its command-palette entries.
 *
 * Positive `isUsRegion`, not `!isIndianRegion`: an unresolved region must not read as
 * "not India" and open the page. Fails closed.
 */
export const canViewCompliance = (meta?: RouteMeta): boolean =>
  !!meta?.isUsRegion && hasModuleView(meta, RbacModules.channelMessage)

export const canViewReports = (meta?: RouteMeta): boolean => hasModuleView(meta, RbacModules.report)

/**
 * Usage reports (billed spend): report-view RBAC, since they expose org-wide spend, and the
 * usageReportsEnabled flag. Shared by the /analytics/usage route and the Analytics sidebar.
 */
export const canViewUsageReports = (meta?: RouteMeta): boolean =>
  canViewReports(meta) && !!meta?.featureFlags?.usageReportsEnabled

/** A Trust & Legal tab: administrators of orgs with trustAndLegalEnabled plus the tab's own flag. */
const canViewTrustAndLegalTab = (
  meta: RouteMeta | undefined,
  tabFlag: 'complianceReportsEnabled' | 'securityAddendumsEnabled',
): boolean =>
  isAdministrator(meta) &&
  !!meta?.featureFlags?.trustAndLegalEnabled &&
  !!meta.featureFlags[tabFlag]

export const canViewSecurityReports = (meta?: RouteMeta): boolean =>
  canViewTrustAndLegalTab(meta, 'complianceReportsEnabled')

/** Also gates the addendum sign and document pages. */
export const canViewSecurityAddendums = (meta?: RouteMeta): boolean =>
  canViewTrustAndLegalTab(meta, 'securityAddendumsEnabled')

/** Geo Permissions (voice, messaging, SIP trunking): administrators and developers. */
export const canViewGeoPermissions = (meta?: RouteMeta): boolean =>
  isAdministrator(meta) || isDeveloper(meta)

export const canViewAnalytics = (meta?: RouteMeta): boolean => {
  // Human specialists cannot see Analytics
  if (isHumanSpecialist(meta)) return false

  const roles = meta?.roles || []

  // Finance analyst cannot see Analytics
  const hasFinanceOnly =
    roles.includes(UserRole.finance_analyst) &&
    !roles.includes(UserRole.administrator) &&
    !roles.includes(UserRole.developer) &&
    !roles.includes(UserRole.support)
  if (hasFinanceOnly) return false

  // Everyone else can see Analytics (admin, developer, support, agent, supervisor)
  return true
}

export const canViewBilling = (meta?: RouteMeta): boolean => {
  // Human specialists cannot see Billing
  if (isHumanSpecialist(meta)) return false

  // Only admin and finance_analyst can see Billing
  if (isAdministrator(meta) || isFinanceAnalyst(meta)) return true

  // Block for developer, support, agent, supervisor, human specialists
  return false
}
